import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

const savePmsSettings = (settings) => axios.post(`${API}/admin/settings`, {
  pms_api_key: settings.pms_api_key || '',
  pms_tenant_slug: settings.pms_tenant_slug || '',
  pms_api_base_url: settings.pms_api_base_url || '',
}, authHeaders());

const PmsField = ({ id, label, hint, type = 'text', placeholder, value, onChange }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} type={type} value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value.trim())}
      autoComplete={type === 'password' ? 'new-password' : 'off'} spellCheck={false}
      className="font-mono text-sm" data-testid={`${id}-input`} />
    {hint && <p className="text-xs text-stone-500 mt-1">{hint}</p>}
  </div>
);

const PmsTestResult = ({ result, onAutofill }) => (
  <div data-testid="pms-test-result" className={`mt-3 p-3 rounded text-sm ${result.success
    ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
    <p className="font-medium">{result.message}</p>
    {result.success && (
      <div className="mt-2 space-y-2 max-h-72 overflow-y-auto">
        {(result.properties || []).map((prop) => (
          <div key={prop.id} className="bg-white p-2 rounded border border-green-300">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium">{prop.name}</p>
                <p className="text-xs">Property ID: <span className="font-mono">{prop.id}</span></p>
              </div>
              <Button size="sm" variant="outline" onClick={() => onAutofill(prop)} data-testid={`pms-autofill-${prop.id}`}>
                Popuni obrazac
              </Button>
            </div>
            <ul className="text-xs ml-2 mt-1">
              {(prop.rooms || []).map((room) => (
                <li key={room.id}>{room.name} (ID: <span className="font-mono">{room.id}</span>)</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    )}
    {!result.success && result.hint && <p className="text-xs mt-1">{result.hint}</p>}
  </div>
);

const PmsWebhookBox = ({ settings, connecting, onConnect }) => (
  <div className="p-4 border rounded-lg bg-slate-50 mt-4">
    <div className="flex items-start justify-between gap-3 mb-3">
      <Label className="text-sm font-semibold">PMS Real-Time Webhook</Label>
      {settings.pms_webhook_id
        ? <span data-testid="pms-webhook-status-connected" className="text-xs font-semibold px-2 py-1 bg-emerald-100 text-emerald-800 rounded">Povezano</span>
        : <span data-testid="pms-webhook-status-disconnected" className="text-xs font-semibold px-2 py-1 bg-gray-200 text-gray-700 rounded">Nije povezano</span>}
    </div>
    {settings.pms_webhook_id && (
      <div className="text-xs font-mono bg-white border rounded p-2 mb-3 space-y-1" data-testid="pms-webhook-info">
        <div>Webhook ID: {settings.pms_webhook_id}</div>
        {settings.pms_webhook_connected_at && <div>Povezano: {new Date(settings.pms_webhook_connected_at).toLocaleString('hr-HR')}</div>}
        {settings.pms_webhook_url && <div className="break-all">URL: {settings.pms_webhook_url}</div>}
      </div>
    )}
    <Button type="button" variant="outline" onClick={onConnect} data-testid="pms-connect-webhook-btn"
      disabled={connecting || !settings.pms_api_key || !settings.pms_tenant_slug || !settings.pms_api_base_url}>
      {connecting ? 'Povezivanje…' : settings.pms_webhook_id ? 'Ponovno poveži / novi secret' : 'Poveži webhook'}
    </Button>
  </div>
);

export const PmsSettingsCard = ({ settings, setSettings, onAutofill }) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const setField = (key) => (value) => setSettings({ ...settings, [key]: value });

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await savePmsSettings(settings);
      const { data } = await axios.get(`${API}/admin/test-pms`, authHeaders());
      setTestResult(data);
      data.success ? toast.success('PMS veza uspješna!') : toast.error('PMS veza nije uspjela');
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || error.message,
        hint: 'Provjerite PMS API ključ, Tenant Slug i Base URL' });
      toast.error('Test PMS veze nije uspio');
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await savePmsSettings(settings);
      const { data } = await axios.post(`${API}/admin/pms/connect-webhook`, {
        webhook_url: `${window.location.origin}/api/pms/webhook`,
        description: `Booking Engine on ${window.location.host}`,
      }, authHeaders());
      toast.success(data.rotated ? 'Webhook rotiran — novi secret spremljen.' : 'Webhook uspješno povezan!');
      const fresh = await axios.get(`${API}/admin/settings`, authHeaders());
      setSettings(fresh.data);
    } catch (error) {
      toast.error(`Povezivanje webhooka nije uspjelo: ${error.response?.data?.detail || error.message}`);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="border-2 border-emerald-200 bg-emerald-50/40 p-4 rounded-lg" data-testid="pms-settings-card">
      <h3 className="text-base font-semibold text-emerald-900 mb-3">RentalWeb PMS (Channel Manager)</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PmsField id="pms-key" label="PMS API ključ" type="password" placeholder="rw_..."
          hint="PMS admin → Settings → API → Rotate API Key" value={settings.pms_api_key} onChange={setField('pms_api_key')} />
        <PmsField id="pms-slug" label="Tenant Slug" placeholder="npr. rijekabooking"
          value={settings.pms_tenant_slug} onChange={setField('pms_tenant_slug')} />
      </div>
      <div className="mt-4">
        <PmsField id="pms-base-url" label="PMS Base URL" placeholder="https://pms.example.com"
          hint="URL vašeg PMS-a (bez /api)" value={settings.pms_api_base_url} onChange={setField('pms_api_base_url')} />
      </div>
      <Button onClick={handleTest} variant="outline" size="sm" className="bg-white mt-3 text-emerald-900 border-emerald-500 hover:bg-emerald-50" data-testid="test-pms-button"
        disabled={testing || !settings.pms_tenant_slug || !settings.pms_api_base_url}>
        {testing ? 'Testiranje...' : 'Testiraj PMS vezu'}
      </Button>
      {testResult && <PmsTestResult result={testResult} onAutofill={onAutofill} />}
      <PmsWebhookBox settings={settings} connecting={connecting} onConnect={handleConnect} />
    </div>
  );
};
